import { Mechanics } from "game-common/mechanics/mechanics"
import { Entity, EntityDimensions, TileSize } from "game-common/models"
import { floorFallZIndex } from "./effects/floor_fall"
import { MOUNT_WAIT_MS, mountOffset } from "game-common/jump"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import type { ClientRenderable } from "./entity_rendering_manager"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

export function initializeEntityDepth(entity: Entity, sprite: ClientRenderable, provider?: TileMapMetaProvider): void {
    if (entity.npcType !== "marker" || !entity.spriteId) return
    const offset = provider?.resolveTileMeta(entity.spriteId)?.renderZIdxOffset
    if (offset !== undefined) sprite.defaultZIndex = offset
    sprite.zIndex = entityDepth(entity, sprite.defaultZIndex, provider)
}

/** Shared by active-room bodies and the projected bodies on other floors. */
export const entityDepth = (entity: Entity, defaultZIndex = 0, provider?: TileMapMetaProvider): number => {
    // A shaft spans several floor renderings; its climber must draw over every section.
    // Standing on either endpoint keeps this priority until the entity steps off.
    if (!entity.floorFall && (entity.stairTravel || provider?.isStairAt(
        Math.floor(entity.location.x / TileSize), Math.floor(entity.location.y / TileSize)))) return 999998

    // A floor-level trap lies under everything that stands.
    if (Mechanics.entity.body.isFlat(entity)) return floorFallZIndex(entity, 0)
    const lying = entity.downed || entity.floorFall?.phase === "landed" || entity.rescuePose === "downed"
    const depth = (Mechanics.entity.body.calculateZIndexBoost(entity) + defaultZIndex || 0) - (lying ? LYING_DEPTH_BIAS : 0)
    const mount = lying ? undefined : mountDepth(entity)
    return floorFallZIndex(entity, storeyDepth(entity, mount !== undefined ? Math.max(depth, mount) : depth, provider, mount !== undefined))
}

/**
 * Over every storey's art -- the storeys above sort at their southern edge, 100 + 32 a row (building_depth.ts), under this
 * on any map under ~2800 rows -- keeping the order of the bodies drawn so among themselves; under the build cursor
 * (100000), the night fog, the leaves and the roofs: a body in front of a storey above that would otherwise be over it.
 */
export const IN_THE_OPEN_DEPTH = 90000

/**
 * The storey above is drawn as one, sorted at its southern edge, so over every body north of that: where it stands right
 * behind a body -- the upper block of a wall two storeys high -- it hid the head, and from a storey down, the face a body
 * just fell past hid it as it came down (or rose past, `rising`: in a jump up onto a wall, from the room it jumped from).
 * So a body is drawn over the storeys above while one of them stands right behind it, or it is in the air between them,
 * as long as nothing on its storey or those above stands in front of it in its column: there, the storey above keeps it
 * under.
 * A body coming down from the storey above is drawn raised over what it drops past, and just over the edge -- soles at
 * the foot of the face below it -- its hip sorted it behind that face (whose pieces sort ~1px past such a hip): at least
 * past every piece of the row north of its soles, then.
 */
const storeyDepth = (entity: Entity, depth: number, provider?: TileMapMetaProvider, rising = false): number => {
    if (!entity.location) return depth
    const fall = entity.floorFall
    const falling = !!fall?.fromAbove && (fall.phase === "falling" || fall.phase === "arriving")
    const x = Math.floor(entity.location.x / TileSize), row = Math.floor((entity.location.y + BODY_GROUND_OFFSET) / TileSize)
    if (falling) depth = Math.max(depth, 100 + row * TileSize + 1)
    else if (!rising && provider) depth = besideRunDepth(provider, entity, depth)
    const upper = provider?.getUpperFloorProvider?.()
    if (!provider || !upper || upper === provider) return depth
    if (!falling && !rising && !DRAWN_ART.some(layerId => drawnAt(upper, layerId, x, row - 1))) return depth
    return openInFront(provider, x, row) ? IN_THE_OPEN_DEPTH + depth / 1000 : depth
}

/**
 * A body standing beside a run of wall or blocks that goes on past it southward -- solid in its row and the next, west
 * or east of it -- is drawn over that run's pieces of the next row: they are drawn a row up (a block's top, a wall's cap),
 * across the body's side, and a bow drawn there, a raised arm, went behind them, into the terrace beside it. Not when
 * something stands in front of it in its own column (a doorway's wall is in front of a body inside it, a hole's rim).
 */
const besideRunDepth = (provider: TileMapMetaProvider, entity: Entity, depth: number): number => {
    const x = Math.floor(entity.location.x / TileSize), row = Math.floor(entity.location.y / TileSize)
    if (standsAt(provider, x, row + 1)) return depth
    const run = (dx: number) => standsAt(provider, x + dx, row) && standsAt(provider, x + dx, row + 1)
    // Past every layer's pieces of the next row (sprited_tile_zindex.ts), short of the row after.
    return run(-1) || run(1) ? Math.max(depth, 100 + (row + 1) * TileSize + TileSize / 3 + 5) : depth
}

/**
 * A body of another storey, drawn in that storey's layer `offset` px down from the viewer's (scene_room_renderer.ts): while
 * it is in the air between storeys -- coming down into that storey, or jumping up out of it -- and nothing stands in front
 * of it where it is drawn, the depth it is drawn at among the viewer's storey's art, over the storeys' layers; else
 * undefined. The layer, drawn as one, hid it there: a body of the storey below rising out of a gap, or falling into one,
 * went behind the face of the terrace it rose past, which is the viewer's storey's art.
 */
export const airborneOverStoreysDepth = (entity: Entity, active: TileMapMetaProvider | undefined, offset: number): number | undefined => {
    if (!active || !entity.location || Mechanics.entity.body.isFlat(entity)) return undefined
    const fall = entity.floorFall
    const falling = !!fall?.fromAbove && (fall.phase === "falling" || fall.phase === "arriving")
    if (!falling && mountDepth(entity) === undefined) return undefined
    const y = entity.location.y + offset
    if (!openInFront(active, Math.floor(entity.location.x / TileSize), Math.floor((y + BODY_GROUND_OFFSET) / TileSize))) return undefined
    return IN_THE_OPEN_DEPTH + (100 + y) / 1000
}

const DRAWN_ART = ["sprite", "sprite2", "sprite3", "map2", "map3"]

// A map that cannot say (not loaded yet, a double in a test) has nothing there: a body's depth never throws.
const drawnAt = (storey: TileMapMetaProvider, layerId: string, x: number, y: number): boolean => {
    try {
        const tile = storey.resolveTileMapFromLayerId?.(layerId)?.[x]?.[y]
        return !!tile && tile !== "0" && !storey.isHiddenTile?.(tile)
    } catch {
        return false
    }
}

/** What stands on a storey's cell: a wall, a block, anything in the way, or furniture -- not the grass or the ivy. */
const standsAt = (storey: TileMapMetaProvider, x: number, y: number): boolean => {
    try {
        return !!storey.isCollisionAt?.(x, y) || !!storey.wallItemAt?.(x, y) || drawnAt(storey, "sprite2", x, y)
    } catch {
        return false
    }
}

/**
 * Whether nothing stands in front of a body in column `x` with its soles in row `row`, on its storey or the few above: in
 * its row or the next (what sorts in front of it there), and above, as far south as is drawn over it (a storey up is drawn
 * a row higher), their floors and roofs too. Its own storey's floor lies under it.
 */
const openInFront = (provider: TileMapMetaProvider, x: number, row: number): boolean => {
    const seen = new Set<TileMapMetaProvider>()
    let storey: TileMapMetaProvider | undefined = provider
    for (let up = 0; storey && up <= 4 && !seen.has(storey); up++, storey = storey.getUpperFloorProvider?.()) {
        seen.add(storey)
        for (let y = row; y <= row + 1 + up; y++) {
            if (standsAt(storey, x, y)) return false
            if (up && ["map2", "map3", "sky"].some(layerId => drawnAt(storey, layerId, x, y))) return false
        }
    }
    return true
}

/**
 * A body lying down (fallen, downed) sorts where it lies, a little under one standing on the same spot -- which then
 * draws over it -- but still over a wall whose foot it lies by. A wall stops a body with its soles at least 8px in front
 * of its foot (a block's face blocks its top 6px, the body's box is round the hip), and that wall's pieces sort 6.8 under
 * such a body's hip (sprited_tile_zindex.ts): under 6.8, then. Sorted at 0, under every wall, a body that fell off a
 * terrace lay behind the face of the block it fell from, all but its feet hidden.
 */
export const LYING_DEPTH_BIAS = 4

/**
 * A body in the air in a jump up onto a wall (AnimationPoseView.mount): drawn rising over the wall, so sorted as standing on
 * its top -- past every piece of the wall's row -- until it lands up there. Sorted by where it took off, a body leaping onto
 * a block beside it was drawn behind that block's face and top, into them.
 * But from behind the wall (north of it), a body stands where the wall's top is drawn -- the top is drawn a row up, a
 * storey high -- and sorted onto it at once it stood up there the moment it jumped, then hopped back up the screen to
 * where it lands: it stays behind until the arc has it as high as it lands (mountOffset), its feet on the top.
 */
const mountDepth = (entity: Entity): number | undefined => {
    const predicted = entity.predictedFlourish
    const own = !!predicted?.view.mount && Date.now() - predicted.at < predicted.view.durationMs + MOUNT_WAIT_MS
    const view = own ? predicted.view : entity.animationPose
    const mount = view?.mount
    if (!mount || mount.y === undefined || (entity.roomId && entity.roomId !== mount.roomId)) return undefined
    if (mount.x !== undefined && behindWall(entity, mount.x, mount.y)) {
        const elapsedMs = own ? Date.now() - predicted.at : view.durationMs - (view.remainingMs ?? 0)
        if (-mountOffset(elapsedMs, mount.dx, mount.dy).y < -mount.dy) return undefined
    }
    return 100 + (mount.y + 1) * TileSize
}

/** Whether a body's box stands north of the wall at (x, y), across from it: behind it, as the camera sees it. */
const behindWall = (entity: Entity, x: number, y: number): boolean => {
    const halfW = EntityDimensions.player.w / 2, halfH = EntityDimensions.player.h / 2
    const { x: hipX, y: hipY } = entity.location
    return hipX + halfW > x * TileSize && hipX - halfW < (x + 1) * TileSize && hipY + halfH <= y * TileSize + 2
}
